<script setup>
import { useField, useFormContext } from 'vee-validate'
import { computed, unref } from 'vue'

const props = defineProps({
  size: {
    type: String,
    default: 'md',
    validator: value => ['sm', 'md', 'lg'].includes(value),
  },
  type: {
    type: String,
    default: 'text',
  },
  name: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: false,
  },
  placeholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  info: {
    type: String,
    default: '',
  },
  // Nome do ícone (ImoIcon) exibido dentro do campo. Vazio = sem ícone.
  icon: {
    type: String,
    default: '',
  },
  iconPosition: {
    type: String,
    default: 'left',
    validator: value => ['left', 'right'].includes(value),
  },
  // Força o input a ser tratado como opcional na UI, ignorando o required do schema.
  // Útil para campos cujo required é condicional via Yup .when() e que devem aparecer
  // como opcionais em determinados fluxos.
  optional: {
    type: Boolean,
    default: false,
  },
})

const model = defineModel({ type: [String, Number] })

// Acompanha a altura do campo — mesmo critério do ImoButton.
const iconSize = computed(() => {
  if (props.size === 'sm') return 16
  return props.size === 'lg' ? 20 : 18
})

// Validate
const { value, errorMessage } = useField(() => props.name, undefined, {
  syncVModel: true,
})

// Acessa o schema do formulário pai via useFormContext — undefined quando input está solto
const schema = useFormContext()?.schema

// Usa o describe() do Yup para verificar se o campo tem a regra required.
// `unref` cobre os dois jeitos de o schema chegar: objeto Yup direto (useForm) ou Ref (<Form>).
const isRequired = computed(() => {
  if (props.optional) return false
  try {
    const fieldSchema = unref(schema)?.describe?.()?.fields?.[props.name]
    return fieldSchema?.tests?.some(t => t.name === 'required') ?? false
  } catch {
    return false
  }
})
</script>

<template>
  <div
    class="flex flex-col gap-1 px-0.5 py-0"
    :class="{
      'text-neutral-40': disabled,
    }"
  >
    <div class="flex items-center gap-2">
      <label
        class="text-neutral-60"
        :class="{
          'text-base': size === 'lg',
          'text-sm': size === 'md',
          'text-xs': size === 'sm',
        }"
        :for="name"
      >
        {{ label }}
        <span v-if="isRequired" class="text-error-60">*</span>
      </label>
    </div>
    <div class="relative flex flex-row">
      <!-- `pointer-events-none` para o clique no ícone chegar ao input e focá-lo. -->
      <span
        v-if="icon"
        class="text-neutral-40 pointer-events-none absolute top-1/2 -translate-y-1/2"
        :class="iconPosition === 'right' ? 'right-3' : 'left-3'"
      >
        <ImoIcon :name="icon" :size="iconSize" />
      </span>
      <input
        class="placeholder:text-neutral-40 w-full rounded-lg"
        :class="{
          error: errorMessage,
          input: !errorMessage,
          'h-[38px]': size === 'md',
          'h-[44px]': size === 'lg',
          'h-[36px]': size === 'sm',
          'border-neutral-30': !model || model.length === 0,
          'border-neutral-40': model && model.length > 0,
          // Abre espaço para o ícone: sem isto o texto encosta nele.
          'pl-10': icon && iconPosition === 'left',
          'pr-10': icon && iconPosition === 'right',
        }"
        v-bind="$attrs"
        :id="$attrs.id"
        :disabled="disabled"
        :type="type"
        :placeholder="placeholder"
        v-model="value"
      />
    </div>
    <span class="text-error-60 text-xs" v-html="errorMessage"></span>
  </div>
</template>

<style scoped>
@reference "@/assets/style/main.css";

.input {
  @apply disabled:bg-neutral-10 disabled:border-neutral-30 disabled:text-neutral-40 text-primary-90;
}

.input:focus-visible {
  @apply border-[#4c94ff73];
  box-shadow: 0px 0px 0px 3px #4c94ff73;
}

.error {
  @apply bg-error-10 border-error-60 text-error-60;
  box-shadow: 0px 0px 0px 3px #f008024d;
}
</style>
